import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Send, Inbox, Receipt, Shield, LogOut, UserCog, Layers, KeyRound, Users, BarChart3, ScrollText, BookOpen, BookUser } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { InstallAppButton, InstallBanner } from "@/components/InstallApp";

const LINKS = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true, id: "overview" },
  { to: "/app/send", label: "Send letter", icon: Send, id: "send" },
  { to: "/app/address-book", label: "Address book", icon: BookUser, id: "address-book" },
  { to: "/app/mailbox", label: "Mailbox", icon: Inbox, id: "mailbox" },
  { to: "/app/receipts", label: "Receipts", icon: Receipt, id: "receipts" },
  { to: "/app/your-data", label: "Your data", icon: UserCog, id: "your-data" },
  { to: "/app/guide", label: "How to send", icon: BookOpen, id: "guide" },
];

const Item = ({ to, label, icon: Icon, end, id }) => (
  <NavLink to={to} end={end} data-testid={`sidebar-${id}-link`}
    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
      isActive ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
    <Icon size={16} /> {label}
  </NavLink>
);

export const AppShell = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const perms = user?.org?.perms || [];
  const ent = user?.enterprise ? [
    { to: "/app/insights", label: "Insights", icon: BarChart3, id: "insights" },
    { to: "/app/bulk", label: "Bulk send", icon: Layers, id: "bulk" },
    ...(perms.includes("keys:manage") ? [{ to: "/app/api", label: "API & usage", icon: KeyRound, id: "api" }] : []),
  ] : [];
  const team = user?.org ? [{ to: "/app/team", label: "Team", icon: Users, id: "team" }] : [];
  const audit = user?.enterprise && perms.includes("members:manage") ? [{ to: "/app/audit", label: "Audit trail", icon: ScrollText, id: "audit" }] : [];
  const base = [...LINKS.slice(0, 2), ...ent, ...LINKS.slice(2), ...team, ...audit];
  const links = user?.role === "admin" ? [...base, { to: "/admin", label: "Admin", icon: Shield, id: "admin" }] : base;
  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <aside className="md:w-64 md:min-h-screen md:sticky md:top-0 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 flex md:flex-col">
        <div className="hidden md:flex h-16 items-center px-5 border-b border-slate-200 dark:border-slate-800"><Logo to="/app" /></div>
        <nav className="flex md:flex-col gap-1 p-3 overflow-x-auto flex-1">
          {links.map((l) => <Item key={l.to} {...l} />)}
        </nav>
        <div className="hidden md:block p-4 border-t border-slate-200 dark:border-slate-800">
          <InstallAppButton className="w-full mb-4" />
          <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          <div className="mt-3 flex items-center gap-2">
            <ThemeToggle />
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-full border border-slate-200 dark:border-slate-800 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </aside>
      <div className="md:hidden flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-800">
        <Logo to="/app" />
        <div className="flex items-center gap-3">
          <InstallAppButton testId="install-app-btn-mobile" className="h-8 px-3 text-xs" />
          <button data-testid="logout-btn-mobile" onClick={async () => { await logout(); nav("/"); }} className="text-sm inline-flex items-center gap-1"><LogOut size={14} /> Sign out</button>
        </div>
      </div>
      <main className="flex-1 min-w-0 px-4 sm:px-8 lg:px-12 py-8 lg:py-10">
        <InstallBanner />
        {user?.role === "admin" && user?.payments_mode === "test" && (
          <div data-testid="payments-test-mode-banner" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
            <span className="font-semibold">Payments are in test mode.</span> No real money is taken. To go live, open Manage → Payments, claim your Stripe account, finish Stripe's checks, then republish. Only admins see this.
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
};
